
import axios from 'axios';

export const getBaseURL = (): string => {
  return import.meta.env.VITE_API_URL || '/api/v1';
};

export const BASE_URL = getBaseURL();

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  preferred_currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface LoginResponseData {
  user: User;
  token: string;
  refresh_token: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: LoginResponseData;
  timestamp: string;
}

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Auth data is written to sessionStorage by default (cleared when the browser/tab closes)
// and only promoted to localStorage (persists across restarts) when "Remember me" is checked.
// Reads check sessionStorage first, then fall back to localStorage, so both modes work
// transparently everywhere else in the app.
const AUTH_KEYS = ['jwt', 'refresh_token', 'isLoggedIn', 'user', 'email', 'userRole', 'userId', 'userProfile', 'tekjuice_sales'] as const;

const getAuthItem = (key: string): string | null => {
  return sessionStorage.getItem(key) ?? localStorage.getItem(key);
};

const setAuthItem = (key: string, value: string, remember: boolean): void => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
  (remember ? localStorage : sessionStorage).setItem(key, value);
};

const removeAuthItem = (key: string): void => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

const logout = () => {
  AUTH_KEYS.forEach(removeAuthItem);

  window.location.href = '/login';

  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('Session Expired', {
      body: 'You have been logged out. Please log in again.',
      icon: '/logo.png'
    });
  }
};

export const storeAuthData = (loginResponse: LoginResponse, remember: boolean = true) => {
  if (loginResponse.success && loginResponse.data) {
    const { user, token, refresh_token } = loginResponse.data;

    setAuthItem('jwt', token, remember);
    setAuthItem('refresh_token', refresh_token, remember);
    setAuthItem('isLoggedIn', 'true', remember);
    setAuthItem('user', JSON.stringify(user), remember);
    setAuthItem('email', user.email, remember);
    setAuthItem('userRole', user.role, remember);
    setAuthItem('userId', user.id, remember);

    return true;
  }
  return false;
};

export const getStoredUser = (): User | null => {
  try {
    const userStr = getAuthItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    return null;
  }
};

export const isLoggedIn = (): boolean => {
  const token = getAuthItem('jwt');
  const loggedIn = getAuthItem('isLoggedIn');
  return !!(token && loggedIn === 'true');
};

export const getAuthToken = (): string | null => {
  return getAuthItem('jwt');
};

export const getRefreshToken = (): string | null => {
  return getAuthItem('refresh_token');
};

export const getStoredValue = (key: string): string | null => {
  return getAuthItem(key);
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      if (error.response.status === 0) {
        console.error('CORS/Network error: Request was blocked');
        throw new Error('Network error: Unable to connect to server. Please check your connection and try again.');
      }
      
      if (error.response.status === 401 || error.response.status === 403) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && !currentPath.includes('/login')) {
          console.warn('Authentication error - logging out');
          logout();
        }
      }
      
      if (error.response.status >= 500) {
        console.error('Server error:', error.response.status);
        throw new Error('Server error: Please try again later.');
      }
    } else if (error.request) {
      console.error('Network error: No response received');
      throw new Error('Network error: Please check your internet connection.');
    }
    
    return Promise.reject(error);
  }
);

export const get = async <T>(url: string, params?: object): Promise<T> => {
  try {
    const response = await axiosInstance.get<T>(url, { params });
    return response.data;
  } catch (error) {
    console.error(`GET ${url} failed:`, error);
    throw error;
  }
};

export const post = async <T>(url: string, data?: object): Promise<T> => {
  try {
    const response = await axiosInstance.post<T>(url, data);
    return response.data;
  } catch (error) {
    console.error(`POST ${url} failed:`, error);
    throw error;
  }
};

export const put = async <T>(url: string, data?: object): Promise<T> => {
  try {
    const response = await axiosInstance.put<T>(url, data);
    return response.data;
  } catch (error) {
    console.error(`PUT ${url} failed:`, error);
    throw error;
  }
};

export const del = async <T>(url: string): Promise<T> => {
  try {
    const response = await axiosInstance.delete<T>(url);
    return response.data;
  } catch (error) {
    console.error(`DELETE ${url} failed:`, error);
    throw error;
  }
};

export const upload = async <T>(url: string, formData: FormData): Promise<T> => {
  try {
    const response = await axiosInstance.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error(`UPLOAD ${url} failed:`, error);
    throw error;
  }
};

export const downloadFile = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await axiosInstance.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error(`DOWNLOAD ${url} failed:`, error);
    throw error;
  }
};

export const patch = async <T>(url: string, data?: object): Promise<T> => {
  try {
    const response = await axiosInstance.patch<T>(url, data);
    return response.data;
  } catch (error) {
    console.error(`PATCH ${url} failed:`, error);
    throw error;
  }
};

// Auth-specific methods
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await post<LoginResponse>('/auth/login', { email, password });
    
    if (response.success) {
      storeAuthData(response);
    }
    
    return response;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

export const refreshToken = async (): Promise<LoginResponse> => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const response = await post<LoginResponse>('/auth/refresh', { refresh_token: refreshToken });
    
    if (response.success) {
      storeAuthData(response);
    }
    
    return response;
  } catch (error) {
    console.error('Token refresh failed:', error);
    logout();
    throw error;
  }
};

export const validateToken = async (): Promise<boolean> => {
  try {
    const token = getAuthToken();
    if (!token) {
      return false;
    }
    
    await get('/auth/validate');
    return true;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
};

export { logout }